/**
 * The two halves of the boundary, on screen: what staff cannot change about this
 * workspace, and what belongs to the workspace and no admin route can reach.
 *
 * WHY A PANEL AND NOT A TOOLTIP. Both lists answer a question staff will
 * otherwise take to a support engineer, and the answer they get there is a
 * database console — the exact unattributable write this whole slice exists to
 * remove the excuse for. A greyed field with no explanation reads as a bug; a
 * missing field reads as an oversight; a sentence saying *why*, and naming the
 * operation that would do it instead, costs one line and removes the reason to
 * route around the panel.
 *
 * IT IS DRIVEN BY THE CONSTANTS, NOT BY A LIST TYPED HERE.
 * `TENANT_FIELDS_ADMIN_MAY_NOT_CHANGE` and `TENANT_DATA_ADMIN_MAY_NOT_REACH`
 * come from `@wend/shared`, and the copy in `lib/tenant-boundary.ts` is typed as
 * a `Record` over each of them — so a name added to either constant fails the
 * typecheck until somebody writes the sentence. The alternative, a hand-written
 * list beside the constants, goes stale in the one direction that matters: the
 * screen keeps confidently describing a boundary the code no longer draws, and
 * nobody re-reads a claim that has always been there.
 *
 * WHAT THIS PANEL IS NOT. It does not enforce anything. `.strict()` on
 * `tenantEditableFieldsSchema` refuses a body naming `subscriptionKey`, and the
 * second list is true because the routes it describes do not exist — not because
 * this component says so. If either of those changes, this panel becomes a lie
 * before it becomes an error, which is why the copy it renders lives next to the
 * constants rather than here.
 */
import { Lock } from 'lucide-react';

import {
  TENANT_DATA_ADMIN_MAY_NOT_REACH,
  TENANT_FIELDS_ADMIN_MAY_NOT_CHANGE,
  type Tenant,
  type TenantFieldAdminMayNotChange,
} from '@wend/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@wend/ui';

import { formatDateTime } from '../../lib/format';
import { UNEDITABLE_FIELD_COPY, UNREACHABLE_DATA_LABEL } from '../../lib/tenant-boundary';

/**
 * The value to show for each un-editable field, or `null` where the read shape
 * does not carry one.
 *
 * `createdByAdminId` is `null` here because `tenantSchema` does not return it —
 * and that is worth showing as "recorded, not shown" rather than omitting the
 * row, because omitting it would make the list on screen shorter than the list
 * the code enforces, which is the drift this panel is typed to prevent.
 */
function uneditableValues(tenant: Tenant): Record<TenantFieldAdminMayNotChange, string | null> {
  return {
    id: tenant.id,
    subscriptionKey: tenant.subscriptionKey,
    createdByAdminId: null,
    createdAt: formatDateTime(tenant.createdAt),
    updatedAt: formatDateTime(tenant.updatedAt),
  };
}

export function TenantBoundaryPanel({ tenant }: { tenant: Tenant }) {
  const values = uneditableValues(tenant);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-[1.02rem]">What staff cannot change</CardTitle>
        <CardDescription>
          The admin panel runs on a database connection that bypasses row-level security — the one
          role in Wend that can see and write every workspace. Everything it may <em>not</em> do is
          therefore written down rather than assumed.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-7">
        <section className="flex flex-col gap-3">
          <h3 className="font-mono text-[0.68rem] uppercase tracking-[0.12em] text-muted">
            Fields on this workspace
          </h3>

          <dl className="flex flex-col">
            {TENANT_FIELDS_ADMIN_MAY_NOT_CHANGE.map((field) => {
              const copy = UNEDITABLE_FIELD_COPY[field];
              const value = values[field];

              return (
                <div
                  key={field}
                  className="flex flex-col gap-1 border-b border-line py-3 first:pt-0 last:border-b-0 sm:flex-row sm:gap-4"
                >
                  <dt className="flex items-center gap-1.5 text-[0.82rem] leading-6 text-muted sm:w-44 sm:shrink-0">
                    <Lock aria-hidden="true" className="size-3.5 shrink-0" />
                    {copy.label}
                  </dt>
                  <dd className="min-w-0">
                    <p className="break-all font-mono text-[0.8rem] leading-6 text-text">
                      {value ?? (
                        <span className="font-sans text-[0.85rem] text-muted">
                          Recorded on the workspace; not shown on this screen.
                        </span>
                      )}
                    </p>
                    <p className="mt-1 max-w-prose text-[0.82rem] leading-relaxed text-muted">
                      {copy.why}
                    </p>
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="font-mono text-[0.68rem] uppercase tracking-[0.12em] text-muted">
            This workspace&rsquo;s own data
          </h3>

          <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
            None of the following is readable or writable from any admin route. If a customer needs
            something changed in here, they change it themselves in their own workspace, or they
            send it to us — staff do not go and look. The one exception is a count: the panel
            above says how many flows and rule segments the workspace has, and nothing about
            what is in them.
          </p>

          <ul className="flex flex-wrap gap-2">
            {TENANT_DATA_ADMIN_MAY_NOT_REACH.map((kind) => (
              <li
                key={kind}
                className="rounded-base border border-line px-2.5 py-1 text-[0.8rem] text-muted"
              >
                {UNREACHABLE_DATA_LABEL[kind]}
              </li>
            ))}
          </ul>
        </section>
      </CardContent>
    </Card>
  );
}
